'use client';

import { useState } from 'react';
import Link from 'next/link';
import { Lightbulb } from 'lucide-react';
import { useBasePath } from '../navigation/base-path';
import { cn } from '../utils/cn';

export interface IdeasTabItem {
  id: string;
  imageUrl: string;
  prompt: string;
  imageWidth?: number | null;
  imageHeight?: number | null;
}

interface IdeasTabContentProps {
  ideas?: IdeasTabItem[];
  isLoading?: boolean;
}

export function IdeasTabContent({
  ideas,
  isLoading = false,
}: IdeasTabContentProps) {
  return (
    <div className="space-y-3">
      {isLoading ? (
        <IdeasSkeleton />
      ) : ideas && ideas.length > 0 ? (
        <IdeasGrid ideas={ideas} />
      ) : (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-card">
            <Lightbulb className="h-8 w-8 text-primary" />
          </div>
          <p className="max-w-[280px] text-sm text-muted-foreground">
            Здесь скоро появятся вдохновляющие идеи!
          </p>
        </div>
      )}
    </div>
  );
}

function IdeasSkeleton() {
  return (
    <div className="flex gap-1">
      <div className="flex flex-1 flex-col gap-1">
        {[180, 220, 160].map((height, index) => (
          <div key={index} className="animate-pulse rounded-sm bg-card" style={{ height }} />
        ))}
      </div>
      <div className="flex flex-1 flex-col gap-1">
        {[220, 160, 200].map((height, index) => (
          <div key={index} className="animate-pulse rounded-sm bg-card" style={{ height }} />
        ))}
      </div>
    </div>
  );
}

function IdeasGrid({ ideas }: { ideas: IdeasTabItem[] }) {
  const balanced = ideas.map((idea) => ({
    ...idea,
    resultWidth: idea.imageWidth,
    resultHeight: idea.imageHeight,
  }));
  const [left, right] = balanceMasonryColumns(balanced);

  return (
    <div className="flex gap-1">
      <div className="flex flex-1 flex-col gap-1">
        {left.map((idea) => (
          <IdeaCard key={idea.id} idea={idea} />
        ))}
      </div>
      <div className="flex flex-1 flex-col gap-1">
        {right.map((idea) => (
          <IdeaCard key={idea.id} idea={idea} />
        ))}
      </div>
    </div>
  );
}

function IdeaCard({ idea }: { idea: IdeasTabItem }) {
  const [loaded, setLoaded] = useState(false);
  const basePath = useBasePath();

  return (
    <Link
      href={`${basePath}/ideas/${idea.id}`}
      className="relative block overflow-hidden bg-card"
      style={
        idea.imageWidth && idea.imageHeight
          ? { aspectRatio: `${idea.imageWidth}/${idea.imageHeight}` }
          : { minHeight: 120 }
      }
    >
      {!loaded && <div className="absolute inset-0 animate-pulse bg-secondary/50" />}
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img
        src={idea.imageUrl}
        alt={idea.prompt}
        className={cn(
          'block h-auto w-full transition-opacity duration-300',
          loaded ? 'opacity-100' : 'opacity-0',
        )}
        loading="lazy"
        onLoad={() => setLoaded(true)}
      />
    </Link>
  );
}

function balanceMasonryColumns<
  T extends { resultWidth?: number | null; resultHeight?: number | null },
>(items: T[]): [T[], T[]] {
  const left: T[] = [];
  const right: T[] = [];
  let leftHeight = 0;
  let rightHeight = 0;

  for (const item of items) {
    const ratio =
      item.resultWidth && item.resultHeight ? item.resultHeight / item.resultWidth : 1;
    if (leftHeight <= rightHeight) {
      left.push(item);
      leftHeight += ratio;
    } else {
      right.push(item);
      rightHeight += ratio;
    }
  }

  return [left, right];
}
